import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, VolumeX } from 'lucide-react';
import { Container, Section, Badge } from '../ui';

const PURPOSE_VIDEO_URL =
  'https://istatec-assets.istatec.com/our_purpose_istatec.mp4';

/**
 * "Our Purpose" section — video/text pair used on About and Contact pages.
 *
 * Playback behavior:
 * - Autoplays with sound when scrolled into view, when the browser allows.
 * - Falls back to muted autoplay if the browser blocks unmuted autoplay
 *   (all modern browsers require a prior user gesture to autoplay with sound).
 *   A "Click for sound" pill lets the visitor turn sound on with one tap.
 * - Pauses when scrolled out of view.
 */
export const OurPurpose: React.FC = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const hasAttemptedUnmutedPlayRef = useRef(false);
  const [isMuted, setIsMuted] = useState(false);

  const tryPlay = useCallback(async () => {
    const video = videoRef.current;
    if (!video) return;

    // On the very first play, try to autoplay with sound. If the browser
    // blocks it (typical on a fresh page load with no user gesture yet),
    // gracefully fall back to muted playback.
    if (!hasAttemptedUnmutedPlayRef.current) {
      hasAttemptedUnmutedPlayRef.current = true;
      try {
        video.muted = false;
        await video.play();
        setIsMuted(false);
        return;
      } catch {
        video.muted = true;
        setIsMuted(true);
        // Fall through to the muted play() below.
      }
    }

    // Subsequent re-entries: resume with whatever mute state the user left it in.
    video.play().catch(() => undefined);
  }, []);

  // Play when the video scrolls into view; pause when it leaves.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          void tryPlay();
        } else {
          video.pause();
        }
      },
      { threshold: 0.4 }, // trigger when ~40% of the video is on-screen
    );

    observer.observe(video);
    return () => observer.disconnect();
  }, [tryPlay]);

  // Keep the "Click for sound" pill in sync if the visitor toggles mute
  // via the native video controls.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const handleVolumeChange = () => setIsMuted(video.muted);
    video.addEventListener('volumechange', handleVolumeChange);
    return () => video.removeEventListener('volumechange', handleVolumeChange);
  }, []);

  const enableSound = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = false;
    setIsMuted(false);
    video.play().catch(() => undefined);
  };

  return (
    <Section background="white" padding="lg">
      <Container>
        {/* 3:2 grid on desktop gives the video a larger, more cinematic footprint */}
        <div className="grid lg:grid-cols-5 gap-10 lg:gap-14 items-center">
          {/* Video (3/5 width on desktop) */}
          <motion.div
            className="relative lg:col-span-3"
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {/* Decorative gradient glow */}
            <div
              aria-hidden="true"
              className="absolute -inset-4 bg-gradient-to-br from-accent-400/30 via-primary-500/20 to-navy-900/10 rounded-3xl blur-2xl"
            />
            <div className="relative overflow-hidden rounded-3xl shadow-2xl ring-1 ring-navy-900/10">
              <video
                ref={videoRef}
                className="w-full h-full object-cover aspect-video bg-navy-950"
                src={PURPOSE_VIDEO_URL}
                loop
                playsInline
                controls
                preload="metadata"
                aria-label="Our purpose at ISTATEC"
              />

              {/* Shown only when the browser forced us into muted playback.
                  One click enables sound. */}
              {isMuted && (
                <button
                  type="button"
                  onClick={enableSound}
                  className="absolute top-4 right-4 flex items-center gap-2 rounded-full bg-navy-900/80 px-3.5 py-2 text-sm font-medium text-white shadow-lg backdrop-blur transition hover:bg-navy-900 focus:outline-none focus:ring-2 focus:ring-accent-500"
                  aria-label="Enable sound"
                >
                  <VolumeX className="w-4 h-4" />
                  Click for sound
                </button>
              )}
            </div>
          </motion.div>

          {/* Content (2/5 width on desktop) */}
          <motion.div
            className="lg:col-span-2"
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <Badge variant="accent" className="mb-4">
              <Sparkles className="w-4 h-4 mr-2" />
              Our Purpose
            </Badge>
            <h2 className="heading-lg text-navy-900 mb-6">
              Our purpose is{' '}
              <span className="bg-gradient-to-r from-accent-500 to-primary-600 bg-clip-text text-transparent">
                simple yet powerful
              </span>
            </h2>
            <p className="text-body text-lg leading-relaxed">
              Together, we shape experiences for better futures. It&rsquo;s what
              drives us to bring people and technology together in ways that
              create meaningful impact and lasting change. From transforming
              lives and enhancing partnerships to building inclusive digital
              experiences, our purpose is the foundation of everything we do. We
              believe that better outcomes begin with better experiences
              &mdash; and we&rsquo;re here to help businesses and communities
              thrive through empathy, innovation, and shared ambition.
            </p>
          </motion.div>
        </div>
      </Container>
    </Section>
  );
};
